<div class="stop-help-languages">
  <h3 class="section-title">Stop &amp; Help Responses</h3>
  <p class="helper-text">
    Both STOP and HELP responses must be configured to activate a language.
    Keyword matching ignores capitals and accents (except in Vietnamese). The
    spaces between the words of a keyword must match exactly.
  </p>

  @if (loadFailed) {
    <p class="load-error" role="alert">
      Could not load this client's languages. Reload the page to try again.
    </p>
  } @else if (selected) {
    <div class="languages-panel">
      <div class="language-list">
        <div class="list-header">Languages</div>
        <ul>
          @for (language of languages; track language.languageCode) {
            <li [class.selected]="language.languageCode === selected.languageCode">
              <button
                type="button"
                class="language-select"
                [attr.aria-current]="language.languageCode === selected.languageCode ? 'true' : null"
                (click)="select(language.languageCode)"
              >
                <span class="language-name">{{ name(language.languageCode) }}</span>
                @if (nativeName(language.languageCode)) {
                  <span class="native-name" [attr.dir]="dir(language.languageCode)">{{
                    nativeName(language.languageCode)
                  }}</span>
                }
              </button>
              <span
                class="status-dot"
                role="img"
                [class.active]="language.status === 'ACTIVE'"
                [attr.aria-label]="language.status === 'ACTIVE' ? 'Active' : 'Incomplete'"
                [attr.title]="language.status === 'ACTIVE' ? 'Active' : 'Incomplete'"
              ></span>
              @if (canEdit && language.languageCode !== english) {
                <button
                  type="button"
                  class="remove-language"
                  [attr.aria-label]="'Remove ' + name(language.languageCode)"
                  (click)="confirmRemoveLanguage(language)"
                >
                  <i class="fa fa-times"></i>
                </button>
              }
            </li>
          }
        </ul>
        @if (canEdit && availableLanguages.length > 0) {
          <div class="list-footer">
            <button
              type="button"
              id="add-language-btn"
              class="btn btn-primary"
              (click)="openAddLanguage()"
            >
              <i class="fa fa-plus"></i> Add
            </button>
          </div>
        }
      </div>

      <div class="language-detail">
        <div class="detail-header">
          <span class="language-name">{{ name(selected.languageCode) }}</span>
          @if (nativeName(selected.languageCode)) {
            <span class="native-name" [attr.dir]="dir(selected.languageCode)">{{
              nativeName(selected.languageCode)
            }}</span>
          }
          <span class="status-badge" [class.active]="selected.status === 'ACTIVE'">{{
            selected.status === 'ACTIVE' ? 'Active' : 'Incomplete'
          }}</span>
        </div>

        <div class="responses">
          @for (type of keywordTypes; track type) {
            <div class="response">
              <label [attr.for]="'sms-' + type + '-response'">{{
                type === 'STOP' ? 'Stop Response' : 'Help Response'
              }}</label>
              <textarea
                class="form-control"
                rows="4"
                [id]="'sms-' + type + '-response'"
                [attr.dir]="dir(selected.languageCode)"
                [attr.maxlength]="textLimit || null"
                [attr.aria-describedby]="
                  selected.languageCode === english
                    ? (type === 'STOP' ? 'stop' : 'help') + '-response-note'
                    : null
                "
                [readonly]="!canEdit"
                [ngModel]="text(type)"
                (ngModelChange)="setText(type, $event)"
              ></textarea>
              <div class="character-count">
                {{ text(type).length }}@if (textLimit) { / {{ textLimit }}}
              </div>

              <div class="trigger-label">
                {{ type === 'STOP' ? 'Stop' : 'Help' }} Keyword Triggers
              </div>
              <div class="keyword-chips" [attr.dir]="dir(selected.languageCode)">
                @for (keyword of systemKeywords(type); track keyword) {
                  <span class="keyword-chip system"><span class="keyword-text">{{ keyword }}</span></span>
                }
                @for (keyword of customKeywords(type); track keyword.id) {
                  <span class="keyword-chip custom">
                    <span class="keyword-text">{{ keyword.keyword }}</span>
                    @if (canEdit) {
                      <button
                        type="button"
                        class="remove-keyword"
                        [attr.aria-label]="'Remove ' + keyword.keyword"
                        (click)="confirmRemoveKeyword(keyword)"
                      >
                        &times;
                      </button>
                    }
                  </span>
                }
              </div>
              @if (canEdit) {
                <div class="add-keyword">
                  <input
                    type="text"
                    class="form-control"
                    dir="auto"
                    maxlength="100"
                    placeholder="Add custom keyword"
                    [attr.aria-label]="'Add custom ' + type + ' keyword'"
                    [ngModel]="keywordInput[type]"
                    (ngModelChange)="onKeywordInput(type, $event)"
                    (keydown.enter)="onKeywordEnter(type, $event)"
                  />
                  <button
                    type="button"
                    class="btn btn-primary"
                    [disabled]="!keywordInput[type].trim() || addingKeyword[type]"
                    (click)="addKeyword(type)"
                  >
                    Add
                  </button>
                </div>
              }
              @if (keywordError[type]) {
                <div class="keyword-error" role="alert">{{ keywordError[type] }}</div>
              }
            </div>
          }
        </div>

        @if (selected.languageCode === english) {
          <div class="english-notes">
            <div class="english-note" id="stop-response-note">
              <p>
                Leave out the Stop All wording unless the program specifically
                requires it. <strong>%s</strong> represents the Product Group
                Feed display name.
              </p>
            </div>
            <div class="english-note" id="help-response-note">
              <p>Product Group information displays here by default.</p>
              <ul>
                <li>
                  First <strong>%s</strong> represents the Product Group Feed
                  display name.
                </li>
                <li>
                  Second <strong>%s</strong> represents the Product Group Help
                  Text including the Relay hosted help site.
                </li>
              </ul>
              <p>
                If specific branding needs to be used for the help site per
                product group, client may choose to use a self-hosted site.
              </p>
              <p>
                If help text is going to be static across the program, a global
                message can be entered here instead of Product Group
                information.
              </p>
            </div>
          </div>
        }
      </div>
    </div>
    <div class="unsaved-changes" role="status">
      @if (hasUnsavedChanges) {
        <span class="error-icon" aria-hidden="true">!</span>
        <span class="error-text">
          You have unsaved changes, please save to keep them
        </span>
      }
    </div>
  }
</div>

<message-dialog
  #addLanguageDialog
  [showAffirm]="false"
  [showCancel]="true"
  [cancelButtonClass]="'btn-link'"
  [verticallyCenter]="true"
  [wide]="true"
>
  <!--
    In an ng-template: message-dialog's @ContentChild(TemplateRef) otherwise
    matches the @for block's template and renders a stray, unbound list row.
  -->
  <ng-template>
    <div class="add-language-dialog">
      <h4>Add Language</h4>
      <p class="helper-text">
        Both STOP and HELP responses must be filled to activate a language.
      </p>
      <ul>
        @for (language of availableLanguages; track language.code) {
          <li>
            <button type="button" (click)="addLanguage(language.code)">
              <span class="language-name">{{ language.englishName }}</span>
              <span class="native-name" [attr.dir]="dir(language.code)">{{
                language.nativeName
              }}</span>
              <span class="system-keywords">
                STOP: {{ language.systemKeywords.STOP.join(', ') }} · HELP:
                {{ language.systemKeywords.HELP.join(', ') }}
              </span>
            </button>
          </li>
        }
      </ul>
    </div>
  </ng-template>
</message-dialog>

<message-dialog
  #removeLanguageDialog
  [showCancel]="true"
  [verticallyCenter]="true"
  affirmButtonText="Remove"
  [affirmButtonClass]="'btn-primary'"
  [cancelButtonClass]="'btn-link'"
  (affirm)="removeLanguage()"
>
  <h4>Remove {{ name(languageToRemove?.languageCode) }}?</h4>
  <p>
    This deletes its STOP and HELP responses, custom keywords and product group
    help text.
  </p>
</message-dialog>

<message-dialog
  #removeKeywordDialog
  [showCancel]="true"
  [verticallyCenter]="true"
  affirmButtonText="Remove"
  [affirmButtonClass]="'btn-primary'"
  [cancelButtonClass]="'btn-link'"
  (affirm)="removeKeyword()"
>
  <h4>Are you sure you want to remove this?</h4>
  <p>'{{ keywordToRemove?.keyword }}' will be removed as a keyword trigger for this language.</p>
</message-dialog>

<message-dialog
  #errorDialog
  [verticallyCenter]="true"
  [affirmButtonClass]="'btn-primary'"
></message-dialog>
